/**
 * Family day view "nothing to order" empty states — one per real-world
 * reason (school break, menu not posted yet, holiday, menu being authored…)
 * instead of a generic "no menu this day". Reason classification lives in
 * `@/lib/family-orders/no-menu-reason`; this file is presentation only.
 */

import { Button } from '@ordo/ui/button'
import { EmptyState } from '@ordo/ui/empty-state'
import {
  CalendarClock,
  CalendarOff,
  CalendarX,
  ChefHat,
  GraduationCap,
  History,
  Sparkles,
  TentTree,
} from 'lucide-react'
import type { ReactElement, ReactNode } from 'react'

import type { NoMenuReason } from '@/lib/family-orders/no-menu-reason'
import { dateFromKey, mondayOf } from '@/lib/school-orders/calendar'

/** "Monday, Jul 6" */
function formatDayLong(key: string): string {
  return dateFromKey(key).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/** "Mon, Aug 24" */
function formatDayShort(key: string): string {
  return dateFromKey(key).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/** "Sep 1, 2025" — the Monday of the key's week. */
function formatWeekOf(key: string): string {
  return dateFromKey(mondayOf(key)).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function Emph({ children }: { children: ReactNode }): ReactElement {
  return <span className="font-medium text-foreground">{children}</span>
}

export function FamilyDayEmptyState({
  reason,
  schoolName,
  date,
  onGoToDate,
  onGoToThisWeek,
}: {
  reason: NoMenuReason
  schoolName: string
  /** The viewed day, `YYYY-MM-DD`. */
  date: string
  /** Jump the day view to another date (e.g. the next menu day). */
  onGoToDate: (dateKey: string) => void
  /** Leave the day view for the current week. */
  onGoToThisWeek: () => void
}): ReactElement {
  let icon: ReactNode
  let title: string
  let description: ReactNode
  let action: ReactNode = null

  switch (reason.kind) {
    case 'school-break':
      icon = <TentTree />
      title = "School's on break"
      description = (
        <>
          No meals are served at {schoolName} right now. Ordering opens back up for{' '}
          <Emph>{formatDayShort(reason.nextMenuStartDate)}</Emph>.
        </>
      )
      action = <Button onClick={() => onGoToDate(reason.nextMenuStartDate)}>Skip to next menu</Button>
      break
    case 'gap-day':
      icon = <CalendarOff />
      title = 'No meal service this day'
      description = (
        <>
          {schoolName} doesn&apos;t have a menu on <Emph>{formatDayLong(date)}</Emph>. The next menu day is{' '}
          <Emph>{formatDayLong(reason.nextMenuStartDate)}</Emph>.
        </>
      )
      action = <Button onClick={() => onGoToDate(reason.nextMenuStartDate)}>Skip to next menu day</Button>
      break
    case 'not-posted-yet':
      icon = <CalendarClock />
      title = "Menu isn't posted yet"
      description = (
        <>
          {schoolName} hasn&apos;t published its menu for this date yet. It&apos;ll appear here as soon as it&apos;s
          ready.
        </>
      )
      break
    case 'before-first-menu':
      icon = <History />
      title = 'Nothing back here'
      description = (
        <>
          Ordering at {schoolName} started the week of <Emph>{formatWeekOf(reason.firstMenuStartDate)}</Emph>. Head back
          to the current week to see what&apos;s coming up.
        </>
      )
      action = <Button onClick={onGoToThisWeek}>Go to this week</Button>
      break
    case 'not-set-up':
      icon = <Sparkles />
      title = 'Something tasty is coming'
      description = (
        <>
          {schoolName} is setting up its kitchen on Ordo. Once everything&apos;s ready, meals will show up right here.
        </>
      )
      break
    case 'menu-pulled':
      icon = <CalendarX />
      title = 'Menu temporarily unavailable'
      description = (
        <>
          The menu for this day is being updated. Check back soon — any order you already placed still appears on your
          Orders page.
        </>
      )
      break
    case 'menu-empty':
      icon = <ChefHat />
      title = 'Menu still in the works'
      description = (
        <>
          We&apos;re finalizing what&apos;s on the menu for this day. Items will appear here once they&apos;re
          confirmed.
        </>
      )
      break
    case 'grade-band-unmapped':
      // Fail-closed grade-band gate: the student's grade isn't mapped to a
      // menu band, so every menu is hidden from them — a school-setup task,
      // not a menu gap. Named per student: a sibling with a mapped band can
      // be ordering fine in the next tab.
      icon = <GraduationCap />
      title = `Menus for ${reason.studentName} aren't available yet`
      description = (
        <>
          <Emph>{reason.studentName}</Emph>&apos;s grade needs to be set up by {schoolName} before meals appear here. If
          this doesn&apos;t change soon, contact the school.
        </>
      )
      break
    case 'no-service-day':
      icon = <CalendarOff />
      title = 'No school meals this day'
      description = (
        <>
          {schoolName} isn&apos;t serving meals on <Emph>{formatDayLong(date)}</Emph>.
          {reason.notes ? ` ${reason.notes.trim().replace(/\.?$/, '.')}` : ''}
        </>
      )
      break
    default:
      icon = <CalendarOff />
      title = 'No menu this day'
      description = (
        <>
          {schoolName} doesn&apos;t have a menu on this date. This usually means school&apos;s on break or the menu
          hasn&apos;t been posted yet.
        </>
      )
      break
  }

  return <EmptyState icon={icon} title={title} description={description} action={action} padding="sm" />
}
